import { useCallback, useMemo, useRef, useState } from 'react'
import { NO_OVERRIDES, beginMove, failMove } from './stageOverrides'
import { EXIT_DECLINED, EXIT_SCHEDULED } from './treatmentStageCatalog'
import { TreatmentBandId } from './treatmentBands'
import type { BookedGroup, TreatmentLane } from 'app/types/treatment'
import { useBandCards, type BandCardsOptions } from './useBandCards'
import { TreatmentCard } from './treatmentFixture'
import { useTreatmentStages } from './useTreatmentStages'
import { notifyError } from 'app/services/notify.client'
import type { StageOutcome } from 'app/types/list-board'
import { logError } from 'lib/logging'

export { BOARD_STAGES, TREATMENT_PIPELINE_ID, TREATMENT_STAGES } from './treatmentStageCatalog'
export type { BoardStage } from './treatmentStageCatalog'

const NO_TERMS: string[] = []

/**
 * How many people the board reads on its own before handing over to "Load more".
 *
 * Enough that most bands arrive whole, so every column, count and value is complete; the oldest
 * bands run past two thousand, and reading all of those unasked would be a hundred requests.
 */
const BACKGROUND_FILL = 500

/**
 * How the cases stack up inside a column.
 *
 * Recency is the default because treatment goes cold: a case planned last
 * week is a far better call than a bigger one planned in February, and
 * sorting purely by money buries the winnable work under the whales.
 */
export type BoardSort = `presented` | `value`

export const BOARD_SORTS: { id: BoardSort; label: string; shortLabel: string }[] = [
  { id: `presented`, label: `Recently presented`, shortLabel: `Recent` },
  { id: `value`, label: `Largest first`, shortLabel: `Largest` },
]

/** A case flattened into a table row: the card, plus the stage it sits at. */
export type TreatmentRow = TreatmentCard & { stage: string }

export type BoardColumn = {
  stage: TreatmentLane
  cards: TreatmentCard[]
  /** Production sitting in this column, which is the number staff care about. */
  value: number
}

/**
 * The unscheduled-treatment board.
 *
 * Cases come from staged data (see `treatmentFixture`) because the API does not
 * carry procedures yet. On the old board a stage, including the two exits
 * (`Scheduled`, `Lost`), is a `system:treatment` pipeline membership on the
 * contact; with grouped navigation on it is the contact's `treatmentStatus`
 * on the Unscheduled Treatment list (see `useTreatmentStages`). Either way it
 * is a shared record: a colleague who moves a card, marks it scheduled,
 * or marks it lost sees that on refresh, not just for the rest of
 * their own browser session.
 *
 * Scheduled and declined used to be a separate local list, on the reasoning
 * that they were actions rather than stages a card rests at. That reasoning
 * was the bug: a `useState` list cannot survive a page refresh, so marking a
 * case scheduled and reloading brought it straight back onto the board. They
 * are ordinary stages now, written through the same `pipelineSource` as every
 * other move, with `exit: true` (set on the pipeline, not here) the only thing
 * that keeps them out of the four rendered columns.
 */
export function useTreatmentBoard(
  opts: {
    sort?: BoardSort
    direction?: `ASC` | `DESC`
    band?: TreatmentBandId
    hidden?: BookedGroup[]
    rule?: BandCardsOptions[`rule`]
    /** The Unscheduled Treatment list leaves its exits' contacts off; a records page shows everyone. */
    hideExits?: boolean
  } = {},
) {
  const { sort = `presented`, direction = `DESC` } = opts
  const stages = useTreatmentStages()

  // The band is the board's whole population, not a filter laid over it: the
  // counts, the column totals and the scheduled tray all have to agree with the
  // number on the card that opened it.
  //
  // Marking a case scheduled or lost writes a pipeline membership;
  // it does not touch `unbookedTreatmentValue` or `lastTreatmentPlanDate`,
  // which is what a band's search actually filters on (see
  // `treatmentBands.ts`). So an exit-stage card keeps matching the band's
  // search exactly as before, and the receipt strip below can read it
  // straight off `inBand` rather than needing a query of its own.
  const {
    cards: inBand,
    loading,
    total,
    error,
    loadMore,
    hasMore,
    reload,
  } = useBandCards({
    band: opts.band,
    hidden: opts.hidden,
    sort,
    direction,
    rule: opts.rule,
    excludeTerms: opts.hideExits ? stages.hiddenTerms : NO_TERMS,
    // Only the list's own board draws the Scheduled column; a records page keeps the plain population.
    population: opts.hideExits ? stages.population : undefined,
    fillUpTo: BACKGROUND_FILL,
  })

  // Optimistic overlay for a move that has been clicked but has not (or has
  // just) landed. A card's real stage comes off its own `pipelines`
  // membership on the old board and its `treatmentStatus` on the list; this
  // only bridges the gap until the band is re-read, the same
  // gap `useContactBoard` bridges by patching its own contact list directly.
  // A pipeline board has no such list of its own to patch, since its cards
  // are recomputed from `useBandCards`, so the bridge lives here instead.
  // Keyed by the stage's full name rather than `BoardStage`, since a card can
  // land on one of the two exits, which are not part of that four-way union.
  const [overrides, setOverrides] = useState(NO_OVERRIDES)
  // Every move takes the next one. Two moves on a card can overlap, and a
  // failure may only undo its own work; see `stageOverrides.ts`.
  const nextToken = useRef(0)
  // A synchronous mirror of the overlay's `owner` map. The rollback itself is
  // guarded inside `failMove`, where it can be tested; this is only so the
  // decision to toast can be made outside the state updater, which React may
  // run twice.
  const owners = useRef<Record<string, number>>({})

  /**
   * How many stage writes have landed this session.
   *
   * The exits count their people with a query of their own, and neither that
   * query nor its variables change when a case is dropped on one, so urql
   * would serve the page-load answer for the rest of the session. This is what
   * tells them to look again.
   */
  const [writes, setWrites] = useState(0)

  const stageNameOf = useCallback(
    (card: TreatmentCard): string => overrides.stages[card.id] ?? stages.stageOf(card),
    [overrides, stages],
  )

  /**
   * Every stage write funnels through here, whether it lands on a working
   * stage or an exit.
   *
   * `known` is the card as the caller already holds it, for a case the band
   * has not loaded. An exit list reads its own stage straight from the API and
   * sorts by recency, so it can hand back somebody who is nowhere in the pages
   * the board has read; without this a move on one of them would find no card
   * and do nothing at all, silently.
   */
  const writeStage = useCallback(
    async (cardId: string, stage: string, known?: TreatmentCard, opts?: { quiet?: boolean }): Promise<StageOutcome> => {
      const card = inBand.find((c) => c.id === cardId) ?? (known?.id === cardId ? known : undefined)
      if (!card) return { ok: false, reason: `This case is no longer on screen` }
      // Dropping a case back in the column it came from is not a move. It
      // used to write the stage the card already held, which is a request
      // sent for a gesture that meant nothing and an error toast whenever
      // that request failed.
      if (stageNameOf(card) === stage) return { ok: false, skipped: true, reason: `Already at this stage` }
      // The API refuses staff moves into or out of the booked stage; every door (drag, dialog, table, bulk) ends here.
      if (stages.bookedStage && (stage === stages.bookedStage.name || stageNameOf(card) === stages.bookedStage.name))
        return { ok: false, skipped: true, reason: `${stages.bookedStage.name} is set by a booking` }
      const previous = overrides.stages[cardId]
      const token = ++nextToken.current
      owners.current[cardId] = token
      setOverrides((prev) => beginMove(prev, cardId, stage, token))
      try {
        await stages.write(card, stage)
        setWrites((n) => n + 1)
        return { ok: true }
      } catch (err) {
        logError(`Error moving Action Center treatment card`, err)
        const reason = err instanceof Error ? err.message : `Couldn't save that move`
        // Silent when a later move has taken the card over: it is showing its
        // own stage and owns its own outcome, and "it's back where it was"
        // would be describing something that did not happen.
        if (owners.current[cardId] !== token) return { ok: false, reason }
        // A bulk run passes `quiet`: its results modal names every failure by
        // patient, so a toast apiece would bury that under itself.
        if (!opts?.quiet) notifyError(`Couldn't save that move. It's back where it was.`)
        setOverrides((prev) => failMove(prev, cardId, previous, token))
        return { ok: false, reason }
      }
    },
    [inBand, overrides, stages, stageNameOf],
  )

  /**
   * Moves a case between the four working stages, and back onto them from an
   * exit. Putting a case back is an ordinary move into `Not contacted`, not
   * the stage it left from: writing the exit overwrote the one membership
   * `system:treatment` carries for this contact, the same way any other move
   * does, so nothing recorded where the card sat before it exited.
   */
  const move = useCallback(
    (cardId: string, laneId: string, known?: TreatmentCard) =>
      writeStage(cardId, stages.lanes.find((lane) => lane.id === laneId)?.label ?? laneId, known),
    [writeStage, stages.lanes],
  )

  /**
   * Scheduled cases leave the board. The practice management system is about to
   * say the same thing on its next sync, so keeping them would be asking staff
   * to clear a queue twice.
   */
  const markScheduled = useCallback(
    (cardId: string, known?: TreatmentCard) => writeStage(cardId, EXIT_SCHEDULED, known),
    [writeStage],
  )

  /**
   * The patient said no. A board with no way to record that forces staff to
   * either leave dead cases in a column forever or pretend they were scheduled,
   * and both make every number above them a lie.
   */
  const decline = useCallback(
    (cardId: string, known?: TreatmentCard) => writeStage(cardId, EXIT_DECLINED, known),
    [writeStage],
  )

  const columns = useMemo<BoardColumn[]>(() => {
    return stages.lanes.map((stage) => {
      // The Scheduled column is worth what is booked, the same figure its cards headline.
      const figure = stage.label === stages.bookedStage?.name ? `scheduledValue` : `value`
      const cards = inBand
        .filter((card) => stageNameOf(card) === stage.label)
        .sort((a, b) =>
          sort === `value` ? b[figure] - a[figure] : (b.presented ?? ``).localeCompare(a.presented ?? ``),
        )
      return { stage, cards, value: cards.reduce((sum, card) => sum + card[figure], 0) }
    })
  }, [inBand, sort, stageNameOf, stages.lanes, stages.bookedStage])

  /**
   * The same cases the columns are built from, flat, each carrying the stage
   * it sits at. The table reads this rather than re-deriving stage itself, so
   * an optimistic move shows in both views off the one `overrides` overlay.
   *
   * Exit-stage cases are in here, unlike `columns`: a table has no drop zones
   * to park them in, and hiding a case the moment somebody marks it scheduled
   * would make the row vanish under the cursor that just changed it.
   */
  const rows = useMemo<TreatmentRow[]>(() => {
    // Ordered here, by the same comparator the columns use, and not left in
    // the order the server sent.
    //
    // The band query sorts on `unbookedTreatmentValue`, the contact's own
    // rollup, but the figure on screen is summed from the procedures (see
    // `buildCard`), and the two diverge: the rollup only refreshes when the
    // contact is next saved. In a board nobody can see that, because a column
    // is a pile. In a table it reads as a sort arrow over rows that are
    // plainly not in order, which is the one thing a sorted column may not be.
    //
    // So the server sort chooses which page of the band is read, and this
    // orders what came back. It is honest about the page in hand and no more:
    // fixing the ordering of the whole band means fixing the rollup, which is
    // the API's to do.
    const ordered = [...inBand].sort((a, b) =>
      sort === `value` ? b.value - a.value : (b.presented ?? ``).localeCompare(a.presented ?? ``),
    )
    if (direction === `ASC`) ordered.reverse()
    return ordered.map((card) => ({ ...card, stage: stageNameOf(card) }))
  }, [inBand, sort, direction, stageNameOf])

  return {
    columns,
    rows,
    move,
    /**
     * Moves a case to a stage named in full, exits included. The board reaches
     * its stages through `move`/`markScheduled`/`decline` because a drag can
     * only land on one kind of target at a time; the table's stage dropdown
     * offers all six in one list and needs one door for them.
     */
    setStage: writeStage,
    /** The stage a case sits at, for one the board was handed rather than read. */
    stageOf: stageNameOf,
    markScheduled,
    decline,
    writes,
    /** What is drawn. A page of the band, not the whole of it. */
    sampleCount: inBand.length,
    loadMore,
    hasMore,
    /** Everyone in the band, so the header can size the job honestly. */
    total,
    loading,
    error: error ?? (stages.failed ? `This list could not be loaded.` : undefined),
    reload,
    lanes: stages.lanes,
    exits: stages.exits,
    destinations: stages.destinations,
    bookedStage: stages.bookedStage,
    listBacked: stages.listBacked,
    list: stages.list,
    reloadList: stages.reloadList,
  }
}
